import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import CloudfiMusicLogo from './CloudfiMusicLogo';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    document.body.style.overflow = '';
  }, [location]);

  const toggleMobile = () => {
    setMobileOpen((v) => {
      document.body.style.overflow = !v ? 'hidden' : '';
      return !v;
    });
  };

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/artists', label: 'Artists' },
    { path: '/releases', label: 'Releases' },
    { path: '/about', label: 'About' },
    { path: '/contact', label: 'Contact' },
  ];

  return (
    <>
      {/* Desktop: logo left + floating pill center */}
      <nav className={`nav2 ${scrolled ? 'nav2-scrolled' : ''}`} data-testid="navbar">
        {/* Logo — fixed left */}
        <Link to="/" className="nav2-logo" data-testid="navbar-logo">
          <CloudfiMusicLogo size={scrolled ? 38 : 46} />
        </Link>

        {/* Floating pill */}
        <div className="nav2-pill">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`nav2-link ${location.pathname === link.path ? 'nav2-active' : ''}`}
              data-testid={`nav-${link.label.toLowerCase()}`}
            >
              {location.pathname === link.path && (
                <motion.span
                  className="nav2-active-bg"
                  layoutId="nav-highlight"
                  transition={{ type: 'spring', stiffness: 350, damping: 30 }}
                />
              )}
              <span className="nav2-link-text">{link.label}</span>
            </Link>
          ))}
        </div>

        {/* Mobile hamburger */}
        <button
          className="nav2-burger"
          onClick={toggleMobile}
          data-testid="mobile-menu-btn"
          aria-label="Toggle menu"
        >
          <span className={`nav2-burger-line ${mobileOpen ? 'open' : ''}`} />
          <span className={`nav2-burger-line ${mobileOpen ? 'open' : ''}`} />
        </button>
      </nav>

      {/* Mobile full-screen overlay */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            className="nav2-mobile"
            initial={{ clipPath: 'circle(0% at calc(100% - 40px) 40px)' }}
            animate={{ clipPath: 'circle(150% at calc(100% - 40px) 40px)' }}
            exit={{ clipPath: 'circle(0% at calc(100% - 40px) 40px)' }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="nav2-mobile-inner">
              {navLinks.map((link, i) => (
                <motion.div
                  key={link.path}
                  initial={{ opacity: 0, x: 40 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.15 + i * 0.06, duration: 0.4 }}
                >
                  <Link
                    to={link.path}
                    className={`nav2-mobile-link ${location.pathname === link.path ? 'nav2-mobile-active' : ''}`}
                    onClick={toggleMobile}
                  >
                    <span className="nav2-mobile-num">0{i + 1}</span>
                    {link.label}
                  </Link>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
